import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, HardDrive, Download, Copy, ShieldCheck, Trash2, RotateCcw, Lock, Server, Check, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "./ui";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtBytes(b) {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(b) / Math.log(1024));
  return `${(b / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
}
function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleString() : "—";
}
function copy(text, label) {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
}

export default function Agents() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [agents, setAgents] = useState([]);
  const [backups, setBackups] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", os: "linux", workload: "" });
  const [creds, setCreds] = useState(null); // { agent_key, agent }
  const [restoreFor, setRestoreFor] = useState(null);

  const load = () => {
    Promise.all([api.get("/agents"), api.get("/backups")])
      .then(([a, b]) => { setAgents(a.data); setBackups(b.data); })
      .catch(() => toast.error("Could not load backup data"));
  };
  useEffect(load, []);

  const register = async () => {
    if (!form.name.trim()) { toast.error("Name the agent"); return; }
    try {
      const { data } = await api.post("/agents", { name: form.name, os: form.os, workload: form.workload || null });
      setCreds(data);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not register agent"); }
  };

  const closeDialog = () => { setOpen(false); setCreds(null); setForm({ name: "", os: "linux", workload: "" }); };

  const revoke = async (id) => {
    try { await api.delete(`/agents/${id}`); toast.success("Agent revoked"); load(); }
    catch (e) { toast.error("Could not revoke"); }
  };

  const del = async (b) => {
    try { await api.delete(`/backups/${b.id}`); toast.success("Backup deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Blocked"); }
  };

  const downloadScript = async () => {
    try {
      const { data } = await api.get("/agent/download-script", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/x-python" }));
      const a = document.createElement("a");
      a.href = url; a.download = "vaultvec_agent.py"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Agent downloaded");
    } catch (e) { toast.error("Download failed"); }
  };

  const configTemplate = (key) => JSON.stringify({
    api_url: API_BASE,
    agent_key: key,
    passphrase: "CHOOSE-A-STRONG-SECRET-YOU-KEEP-SAFE",
    retention_days: 30,
    sources: [
      { type: "files", name: "LIMS config & records", paths: ["/etc/lims", "/var/lib/lims/data"] },
      { type: "database", name: "LIMS DB", command: "pg_dump -U postgres lims" },
    ],
  }, null, 2);

  const isLocked = (b) => b.immutable_until && new Date(b.immutable_until) > new Date();

  return (
    <div>
      <PageHeader eyebrow="REAL BACKUPS" title="Backup Agents" subtitle="Install agents on your servers to run encrypted, immutable backups into the Vaultvec vault">
        {isAdmin && (
          <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeDialog())}>
            <DialogTrigger asChild>
              <Button data-testid="register-agent-btn" className="rounded-sm bg-primary hover:bg-primary/90 text-white glow-primary">
                <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Register Agent
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-background border-border rounded-sm max-w-lg">
              {!creds ? (
                <>
                  <DialogHeader>
                    <DialogTitle>Register a backup agent</DialogTitle>
                    <DialogDescription>Create credentials for a server you want to protect. You'll get a one-time agent key.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div><Label>Agent name</Label>
                      <Input data-testid="agent-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="LIMS production server" className="rounded-sm mt-1.5" />
                    </div>
                    <div><Label>Operating system</Label>
                      <Select value={form.os} onValueChange={(v) => setForm({ ...form, os: v })}>
                        <SelectTrigger data-testid="agent-os-select" className="rounded-sm mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="linux">Linux</SelectItem>
                          <SelectItem value="windows">Windows</SelectItem>
                          <SelectItem value="macos">macOS</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Linked workload (optional)</Label>
                      <Input data-testid="agent-workload-input" value={form.workload} onChange={(e) => setForm({ ...form, workload: e.target.value })} placeholder="LIMS Primary" className="rounded-sm mt-1.5" />
                    </div>
                  </div>
                  <Button data-testid="agent-create-submit" onClick={register} className="w-full rounded-sm bg-primary hover:bg-primary/90 text-white">Create agent</Button>
                </>
              ) : (
                <div data-testid="agent-creds">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[hsl(var(--chart-2))]" /> Agent created</DialogTitle>
                    <DialogDescription>Save this key now — it is shown only once and cannot be retrieved later.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-2">
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-widest">Agent key (one-time)</Label>
                      <div className="flex items-center gap-2 mt-1.5">
                        <code data-testid="agent-key-value" className="flex-1 bg-black/50 border border-border rounded-sm px-3 py-2 font-mono text-xs break-all">{creds.agent_key}</code>
                        <Button size="icon" variant="outline" className="rounded-sm shrink-0" onClick={() => copy(creds.agent_key, "Agent key")}><Copy className="h-4 w-4" /></Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <Button data-testid="download-agent-btn" onClick={downloadScript} className="rounded-sm bg-secondary hover:bg-white/10 text-sm"><Download className="h-4 w-4 mr-1" /> Download agent</Button>
                      <Button variant="outline" className="rounded-sm text-sm" onClick={() => copy(configTemplate(creds.agent_key), "Config")}><Copy className="h-4 w-4 mr-1" /> Copy config</Button>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-widest">Setup (on your server)</Label>
                      <pre className="bg-black/50 border border-border rounded-sm p-3 mt-1.5 font-mono text-[11px] leading-relaxed overflow-x-auto">{`# 1. Install deps
python3 -m pip install requests cryptography

# 2. Save config as vaultvec-agent.json (see 'Copy config')
# 3. Run a backup
python3 vaultvec_agent.py backup`}</pre>
                    </div>
                    <p className="text-[11px] text-muted-foreground">Your passphrase encrypts data on the server before upload. Vaultvec never receives it — keep it safe, it is required to restore.</p>
                  </div>
                  <Button data-testid="agent-done-btn" onClick={closeDialog} className="w-full rounded-sm bg-primary hover:bg-primary/90 text-white">Done</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="p-8 space-y-8">
        {/* Agents */}
        <div>
          <h2 className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-3">Registered Agents</h2>
          <div className="card-elevated rounded-sm overflow-hidden" data-testid="agents-table">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs text-muted-foreground uppercase tracking-widest">
                <th className="p-4 font-normal">Agent</th><th className="p-4 font-normal">OS</th><th className="p-4 font-normal">Workload</th><th className="p-4 font-normal">Status</th><th className="p-4 font-normal">Last seen</th>{isAdmin && <th className="p-4 font-normal"></th>}
              </tr></thead>
              <tbody>
                {agents.length === 0 && <tr><td colSpan={isAdmin ? 6 : 5} className="p-8 text-center text-muted-foreground text-sm">No agents yet. Register one to start real backups.</td></tr>}
                {agents.map((a) => (
                  <tr key={a.id} className="border-b border-border last:border-0 hover:bg-white/[0.02] transition-colors">
                    <td className="p-4 flex items-center gap-2"><Server className="h-4 w-4 text-primary" strokeWidth={1.5} /> {a.name}</td>
                    <td className="p-4 font-mono text-xs uppercase">{a.os}</td>
                    <td className="p-4 text-muted-foreground">{a.workload || "—"}</td>
                    <td className="p-4"><StatusBadge value={a.status === "online" ? "active" : a.status === "revoked" ? "at-risk" : "pending"} /></td>
                    <td className="p-4 font-mono text-xs text-muted-foreground">{fmtDate(a.last_seen)}</td>
                    {isAdmin && <td className="p-4 text-right"><Button size="sm" variant="ghost" className="rounded-sm text-muted-foreground hover:text-primary" data-testid={`revoke-${a.id}`} onClick={() => revoke(a.id)}><Trash2 className="h-4 w-4" /></Button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Restore points */}
        <div>
          <h2 className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-3">Restore Points <span className="text-[hsl(var(--chart-2))]">· encrypted &amp; immutable</span></h2>
          <div className="card-elevated rounded-sm overflow-hidden" data-testid="backups-table">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs text-muted-foreground uppercase tracking-widest">
                <th className="p-4 font-normal">Source</th><th className="p-4 font-normal">Agent</th><th className="p-4 font-normal">Type</th><th className="p-4 font-normal">Size</th><th className="p-4 font-normal">Integrity</th><th className="p-4 font-normal">Created</th><th className="p-4 font-normal">WORM until</th><th className="p-4 font-normal"></th>
              </tr></thead>
              <tbody>
                {backups.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-muted-foreground text-sm">No restore points yet. Run <code className="font-mono">python3 vaultvec_agent.py backup</code> on an agent.</td></tr>}
                {backups.map((b) => (
                  <tr key={b.id} className="border-b border-border last:border-0 hover:bg-white/[0.02] transition-colors">
                    <td className="p-4">{b.source_desc}</td>
                    <td className="p-4 text-muted-foreground">{b.agent_name}</td>
                    <td className="p-4 font-mono text-xs uppercase flex items-center gap-1.5">{b.source_type === "database" ? <Database className="h-3.5 w-3.5" /> : <HardDrive className="h-3.5 w-3.5" />}{b.source_type}</td>
                    <td className="p-4 font-mono text-xs">{fmtBytes(b.size_bytes)}</td>
                    <td className="p-4">{b.verified ? <span className="inline-flex items-center gap-1 text-[hsl(var(--chart-2))] text-xs"><Check className="h-3.5 w-3.5" strokeWidth={2.5} /> Verified</span> : <span className="text-primary text-xs">Unverified</span>}</td>
                    <td className="p-4 font-mono text-xs text-muted-foreground">{fmtDate(b.created_at)}</td>
                    <td className="p-4 font-mono text-xs">{isLocked(b) ? <span className="inline-flex items-center gap-1 text-[hsl(var(--chart-4))]"><Lock className="h-3 w-3" /> {new Date(b.immutable_until).toLocaleDateString()}</span> : <span className="text-muted-foreground">expired</span>}</td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="rounded-sm text-muted-foreground hover:text-foreground" data-testid={`restore-${b.id}`} onClick={() => setRestoreFor(b)}><RotateCcw className="h-4 w-4" /></Button>
                      {isAdmin && <Button size="sm" variant="ghost" disabled={isLocked(b)} title={isLocked(b) ? `WORM-locked until ${fmtDate(b.immutable_until)}` : "Delete"} className="rounded-sm text-muted-foreground hover:text-primary disabled:opacity-30" data-testid={`del-backup-${b.id}`} onClick={() => del(b)}><Trash2 className="h-4 w-4" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Restore instructions */}
      <Dialog open={!!restoreFor} onOpenChange={(o) => !o && setRestoreFor(null)}>
        <DialogContent className="bg-background border-border rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5 text-primary" /> Restore this backup</DialogTitle>
            <DialogDescription>Run this on the agent machine (with the same passphrase used to back up). Data is decrypted locally.</DialogDescription>
          </DialogHeader>
          {restoreFor && (
            <div className="space-y-3 py-2">
              <div className="text-sm"><span className="text-muted-foreground">Source:</span> {restoreFor.source_desc}</div>
              <pre className="bg-black/50 border border-border rounded-sm p-3 font-mono text-[11px] overflow-x-auto">{`python3 vaultvec_agent.py restore \\\n  --backup-id ${restoreFor.id} \\\n  --dest ./restored`}</pre>
              <Button className="w-full rounded-sm bg-secondary hover:bg-white/10 text-sm" onClick={() => copy(`python3 vaultvec_agent.py restore --backup-id ${restoreFor.id} --dest ./restored`, "Restore command")}><Copy className="h-4 w-4 mr-1" /> Copy command</Button>
              <p className="text-[11px] text-muted-foreground">Vaultvec verifies the ciphertext hash before decrypting; the agent then verifies your data. Zero-knowledge: without your passphrase, no one can read this backup.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
